import { ViewportScroller } from '@angular/common'
import {
  DestroyRef,
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideEnvironmentInitializer,
} from '@angular/core'
import { NavigationEnd, Router, Scroll } from '@angular/router'

/**
 * Scroll handling for the whole app. Registered in app.config alongside
 * withInMemoryScrolling({ scrollPositionRestoration: 'disabled' }), which keeps the
 * router recording positions and emitting Scroll events but leaves acting on them to us.
 *
 * Why not the router's own 'enabled' mode:
 * - It scrolls to the top on every navigation, including a query-param-only one. Filters
 *   that write the URL (admin search, one-shot deep links) would jump the page on every
 *   keystroke.
 * - It restores Back once, on the next frame. Our lists arrive by HTTP after that, the
 *   page is still short, and the browser clamps the position to the top — Back lands at
 *   the top of a long list and looks like the wrong page.
 *
 * So: Back/Forward restores the saved position and keeps trying while the content loads;
 * a click to a different page goes to the top; a change of query params alone stays put.
 */
export function provideScrollRestoration(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideEnvironmentInitializer(() => {
      const router = inject(Router)
      const scroller = inject(ViewportScroller)
      const destroyRef = inject(DestroyRef)

      // The browser's own restore fires before our content exists; it only gets in the way.
      scroller.setHistoryScrollRestoration('manual')

      let lastPath = ''
      let cancelRestore: (() => void) | null = null

      const sub = router.events.subscribe((e) => {
        if (!(e instanceof Scroll)) return
        cancelRestore?.()
        cancelRestore = null

        const path = e.routerEvent instanceof NavigationEnd
          ? pathOf(e.routerEvent.urlAfterRedirects)
          : lastPath
        const samePage = path === lastPath
        lastPath = path

        if (e.position) {
          cancelRestore = restoreWhenTallEnough(scroller, e.position)
        } else if (e.anchor) {
          scroller.scrollToAnchor(e.anchor)
        } else if (!samePage) {
          scroller.scrollToPosition([0, 0])
        }
      })

      destroyRef.onDestroy(() => {
        sub.unsubscribe()
        cancelRestore?.()
      })
    }),
  ])
}

function pathOf(url: string): string {
  return url.split(/[?#]/)[0]
}

/**
 * Scrolls to `position`, re-trying as the page grows until it gets there, the content has
 * had ~3 s to load, or the user scrolls themselves (their input always wins).
 */
function restoreWhenTallEnough(
  scroller: ViewportScroller,
  position: [number, number],
): () => void {
  const [, targetY] = position
  const deadline = Date.now() + 3000
  let frame = 0

  const stop = () => {
    cancelAnimationFrame(frame)
    for (const ev of USER_SCROLL_EVENTS) window.removeEventListener(ev, stop)
  }
  for (const ev of USER_SCROLL_EVENTS) window.addEventListener(ev, stop, { passive: true, once: true })

  const attempt = () => {
    scroller.scrollToPosition(position)
    const reached = Math.abs(window.scrollY - targetY) <= 2
    if (reached || Date.now() > deadline) {
      stop()
      return
    }
    frame = requestAnimationFrame(attempt)
  }
  attempt()
  return stop
}

const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const
